// <hub-login></hub-login> — drop-in custom element, same usage shape as a
// third-party chat widget:
//
//   <script src="https://host/hub-login/v1/hub-login.js"></script>
//   <hub-login></hub-login>
//
// Two modes:
//   • empty element  -> renders its own styled button in a shadow root
//   • with children  -> renders nothing, just makes the children clickable,
//                       so the site keeps its own markup and CSS
import { applyElementConfig, open } from './index.js';

const BUTTON_CSS = `
:host { display: inline-block; }
button {
  font: inherit;
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: var(--hub-login-font-size, 14px);
  font-weight: 700;
  letter-spacing: .3px;
  color: var(--hub-login-color, #fff);
  background: var(--hub-login-bg, #16407C);
  border: none;
  border-radius: var(--hub-login-radius, 999px);
  padding: var(--hub-login-padding, 11px 26px);
  cursor: pointer;
  transition: filter .2s ease;
}
button:hover { filter: brightness(1.12); }
button:focus-visible { outline: 2px solid var(--hub-login-bg, #16407C); outline-offset: 2px; }
`;

// element attribute -> SDK config
function optionsFromAttributes(el) {
  const cfg = {};
  const theme = {};
  const attr = (n) => (el.hasAttribute(n) ? el.getAttribute(n) : null);

  if (attr('session-endpoint')) cfg.sessionEndpoint = attr('session-endpoint');
  if (attr('base-url')) cfg.baseUrl = attr('base-url');
  if (attr('public-key')) cfg.publicKey = attr('public-key');
  if (attr('private-key')) cfg.privateKey = attr('private-key');
  if (attr('redirect')) cfg.redirect = attr('redirect');

  if (attr('title')) theme.title = attr('title');
  if (attr('subtitle')) theme.subtitle = attr('subtitle');
  if (attr('logo')) theme.logo = attr('logo');
  if (attr('primary-color')) theme.primaryColor = attr('primary-color');
  if (attr('accent-color')) theme.accentColor = attr('accent-color');

  if (Object.keys(theme).length) cfg.theme = theme;
  return cfg;
}

class HubLoginElement extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    // Applied at click time, not now: there is a single shared modal, so
    // config must belong to whichever element the user actually clicked.
    const overrides = optionsFromAttributes(this);

    const launch = (event) => {
      event.preventDefault();
      try {
        // Always applied (even when empty) so this element's look is exactly
        // what its own attributes say, regardless of what was clicked before.
        applyElementConfig(overrides);
      } catch (err) {
        console.error('[HubLogin] <hub-login> config failed:', err);
      }
      open();
    };

    // Site supplied its own markup — keep it, just make it clickable.
    if (this.children.length > 0) {
      this.addEventListener('click', launch);
      return;
    }

    const root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = BUTTON_CSS;

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('part', 'button');
    button.textContent = this.getAttribute('label') || 'Sign in';
    button.addEventListener('click', launch);

    root.append(style, button);
  }
}

export function defineElement() {
  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('hub-login')) return;
  window.customElements.define('hub-login', HubLoginElement);
}
